import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Bot, Mic, Pause, Play, RotateCcw, Square, Sparkles, X } from "lucide-react";
import type { VoicePatientDraft, VoicePatientDraftField } from "../services/patientVoiceIntake";
import { extractPatientDataFromTranscript, transcribePatientAudio } from "../services/patientVoiceIntake";
import { useLanguage } from "../contexts/LanguageContext";

type RecordingStatus = "idle" | "recording" | "paused" | "processing" | "ready" | "error";

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
};

type PatientVoiceIntakeModalProps = {
  open: boolean;
  onClose: () => void;
  onApplyDraft: (draft: VoicePatientDraft) => void;
};

const getSpeechRecognitionCtor = () => {
  if (typeof window === "undefined") return null;
  const globalWindow = window as Window & {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };

  return globalWindow.SpeechRecognition || globalWindow.webkitSpeechRecognition || null;
};

const formatDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
};

const draftFields: Array<{ key: VoicePatientDraftField; label: string; multiline?: boolean }> = [
  { key: "name", label: "patientFieldName" },
  { key: "age", label: "patientFieldAge" },
  { key: "sex", label: "patientFieldSex" },
  { key: "chiefComplaint", label: "patientChiefComplaint", multiline: true },
  { key: "historyOfPresentIllness", label: "patientHistoryOfPresentIllness" },
  { key: "painLocation", label: "patientPainLocation" },
  { key: "symptomDuration", label: "patientSymptomDuration" },
  { key: "traumaMechanism", label: "patientTraumaMechanism", multiline: true },
  { key: "functionalLimitation", label: "patientFunctionalLimitation" },
  { key: "physicalExam", label: "patientPhysicalExam" },
  { key: "suspectedDiagnosis", label: "patientSuspectedDiagnosis" },
  { key: "initialPlan", label: "patientInitialPlan" },
  { key: "notes", label: "patientNotes", multiline: true },
];

const formatConfidence = (score?: number) => {
  if (score == null) return "";
  return `${Math.round(score * 100)}%`;
};

const getIsLowPerformanceMode = () => {
  if (typeof navigator === "undefined") return false;
  const hardwareConcurrency = navigator.hardwareConcurrency || 0;
  const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0;
  return Boolean(saveData) || (hardwareConcurrency > 0 && hardwareConcurrency <= 4) || (deviceMemory > 0 && deviceMemory <= 4);
};

type DraftFieldCardProps = {
  field: { key: VoicePatientDraftField; label: string; multiline?: boolean };
  value: string;
  confidence?: number;
  isLowConfidence: boolean;
  isAutofilled: boolean;
  onChange: (field: VoicePatientDraftField, value: string) => void;
};

const DraftFieldCard = memo(function DraftFieldCard({
  field,
  value,
  confidence,
  isLowConfidence,
  isAutofilled,
  onChange,
}: DraftFieldCardProps) {
  const { t } = useLanguage();
  const wrapperClassName = isLowConfidence
    ? "border-amber-300/30 bg-amber-500/10"
    : isAutofilled
      ? "border-cyan-300/25 bg-cyan-500/10"
      : "border-white/8 bg-white/5";

  return (
    <div className={`rounded-2xl border p-3 transition-colors ${wrapperClassName}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{t(field.label)}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {isAutofilled && (
            <span className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-cyan-100">
              {t('voiceFilledByAI')}
            </span>
          )}
          {isLowConfidence && (
            <span className="rounded-full border border-amber-300/20 bg-amber-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-amber-100">
              {t('voiceLowConfidence')}
            </span>
          )}
          {confidence != null && (
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] font-semibold text-slate-300">
              {formatConfidence(confidence)}
            </span>
          )}
        </div>
      </div>

      {field.key === "sex" ? (
        <select
          value={value}
          onChange={(event) => onChange(field.key, event.target.value)}
          className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
        >
          <option value="">{t('notProvided')}</option>
          <option value="M">{t('patientSexMaleShort')}</option>
          <option value="F">{t('patientSexFemaleShort')}</option>
        </select>
      ) : field.multiline ? (
        <textarea
          value={value}
          onChange={(event) => onChange(field.key, event.target.value)}
          placeholder={t('notProvided')}
          rows={field.key === "notes" ? 4 : 3}
          className="mt-2 min-h-[84px] w-full resize-y rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
        />
      ) : (
        <input
          value={value}
          onChange={(event) => onChange(field.key, event.target.value)}
          placeholder={t('notProvided')}
          inputMode={field.key === "age" ? "numeric" : "text"}
          className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40"
        />
      )}
    </div>
  );
});

const PatientVoiceIntakeModal: React.FC<PatientVoiceIntakeModalProps> = ({
  open,
  onClose,
  onApplyDraft,
}) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [seconds, setSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState("");
  const [transcript, setTranscript] = useState("");
  const [draft, setDraft] = useState<VoicePatientDraft | null>(null);
  const [error, setError] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLowPerformanceMode, setIsLowPerformanceMode] = useState(false);

  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const timerRef = useRef<number | null>(null);
  const transcriptRef = useRef("");
  const transcriptFlushRef = useRef<number | null>(null);
  const pendingBlobResolveRef = useRef<((blob: Blob) => void) | null>(null);
  const audioUrlRef = useRef("");
  const ignoreRecorderStopRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearTranscriptFlush = useCallback(() => {
    if (transcriptFlushRef.current != null) {
      window.clearTimeout(transcriptFlushRef.current);
      transcriptFlushRef.current = null;
    }
  }, []);

  const stopRecognition = useCallback(() => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    try {
      recognition?.stop();
    } catch {
      // noop
    }
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const resetAudioUrl = useCallback(() => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = "";
    }
    setAudioUrl("");
  }, []);

  const resetState = useCallback((options?: { keepRecorderStopGuard?: boolean }) => {
    clearTimer();
    clearTranscriptFlush();
    stopRecognition();
    stopStream();
    recorderRef.current = null;
    chunksRef.current = [];
    pendingBlobResolveRef.current = null;
    if (!options?.keepRecorderStopGuard) {
      ignoreRecorderStopRef.current = false;
    }
    setStatus("idle");
    setSeconds(0);
    setTranscript("");
    transcriptRef.current = "";
    setDraft(null);
    setError("");
    setIsAnalyzing(false);
    resetAudioUrl();
  }, [clearTimer, clearTranscriptFlush, resetAudioUrl, stopRecognition, stopStream]);

  useEffect(() => {
    setIsLowPerformanceMode(getIsLowPerformanceMode());
    if (!open) {
      resetState();
    }

    return () => {
      clearTimer();
      clearTranscriptFlush();
      stopRecognition();
      stopStream();
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = "";
      }
    };
  }, [clearTimer, clearTranscriptFlush, open, resetState, stopRecognition, stopStream]);

  const scheduleTranscriptUpdate = useCallback((nextTranscript: string) => {
    transcriptRef.current = nextTranscript;
    if (transcriptFlushRef.current != null) return;

    transcriptFlushRef.current = window.setTimeout(() => {
      transcriptFlushRef.current = null;
      setTranscript((current) => (current === transcriptRef.current ? current : transcriptRef.current));
    }, 450);
  }, []);

  const startRecognition = useCallback(() => {
    const RecognitionCtor = getSpeechRecognitionCtor();
    if (!RecognitionCtor) return false;

    const recognition = new RecognitionCtor();
    recognition.lang = "pt-BR";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event: any) => {
      const nextTranscript = Array.from(event.results)
        .map((result: any) => result?.[0]?.transcript || "")
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();

      if (nextTranscript) {
        scheduleTranscriptUpdate(nextTranscript);
      }
    };
    recognition.onerror = () => {
      setError(t('voiceRealtimeUnavailable'));
    };
    recognition.onend = () => {
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      return true;
    } catch {
      recognitionRef.current = null;
      return false;
    }
  }, [scheduleTranscriptUpdate]);

  const getRecordedBlob = useCallback(async () => {
    return new Promise<Blob>((resolve) => {
      pendingBlobResolveRef.current = resolve;

      const recorder = recorderRef.current;
      if (!recorder) {
        resolve(new Blob());
        return;
      }

      try {
        recorder.stop();
      } catch {
        resolve(new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" }));
      }
    });
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError("");
      setDraft(null);
      setTranscript("");
      transcriptRef.current = "";
      clearTranscriptFlush();
      resetAudioUrl();
      chunksRef.current = [];
      ignoreRecorderStopRef.current = false;
      setSeconds(0);
      setStatus("processing");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          if (chunksRef.current.length > 20) {
            chunksRef.current.shift();
          }
        }
      };

      recorder.onstop = () => {
        if (ignoreRecorderStopRef.current) {
          if (pendingBlobResolveRef.current) {
            pendingBlobResolveRef.current(new Blob());
            pendingBlobResolveRef.current = null;
          }
          return;
        }

        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        if (pendingBlobResolveRef.current) {
          pendingBlobResolveRef.current(blob);
          pendingBlobResolveRef.current = null;
        }
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
        }
        audioUrlRef.current = URL.createObjectURL(blob);
        setAudioUrl(audioUrlRef.current);
        stopStream();
      };

      recorder.start(1000);
      const recognitionStarted = startRecognition();
      if (!recognitionStarted) {
      setError(t('voiceRealtimeUnavailable'));
      }

      setStatus("recording");
      timerRef.current = window.setInterval(() => setSeconds((value) => value + 1), 1000);
    } catch (err) {
      console.error("[PatientVoiceIntakeModal] failed to start recording", err);
      setStatus("error");
      setError(t('voiceStartRecordingError'));
      stopStream();
    }
  }, [clearTranscriptFlush, resetAudioUrl, startRecognition, stopStream]);

  const pauseRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || status !== "recording") return;

    try {
      recorder.pause();
      stopRecognition();
      setStatus("paused");
    } catch (err) {
      console.error("[PatientVoiceIntakeModal] failed to pause recording", err);
    }
  }, [status, stopRecognition]);

  const resumeRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || status !== "paused") return;

    try {
      recorder.resume();
      startRecognition();
      setStatus("recording");
    } catch (err) {
      console.error("[PatientVoiceIntakeModal] failed to resume recording", err);
    }
  }, [startRecognition, status]);

  const analyzeRecording = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    try {
      setIsAnalyzing(true);
      setError("");
      clearTimer();
      clearTranscriptFlush();
      stopRecognition();

      const recordedBlob = await getRecordedBlob();
      const resolvedTranscript = await transcribePatientAudio(recordedBlob, {
        fallbackTranscript: transcriptRef.current || transcript,
        language: "pt-BR",
      });

      const resolvedDraft = await extractPatientDataFromTranscript(resolvedTranscript);
      setTranscript(resolvedTranscript);
      transcriptRef.current = resolvedTranscript;
      setDraft(resolvedDraft);
      setStatus("ready");
    } catch (err) {
      console.error("[PatientVoiceIntakeModal] failed to analyze recording", err);
      setStatus("error");
      setError(t('voiceAnalyzeRecordingError'));
    } finally {
      setIsAnalyzing(false);
    }
  }, [clearTimer, clearTranscriptFlush, getRecordedBlob, stopRecognition, transcript]);

  const cancelRecording = useCallback(() => {
    clearTimer();
    clearTranscriptFlush();
    stopRecognition();
    ignoreRecorderStopRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch {
        // noop
      }
    }
    resetState({ keepRecorderStopGuard: true });
    onClose();
  }, [clearTimer, clearTranscriptFlush, onClose, resetState, stopRecognition]);

  const finalizeRecording = useCallback(async () => {
    if (status === "idle") return;
    if (status === "recording" || status === "paused") {
      await analyzeRecording();
    }
  }, [analyzeRecording, status]);

  const handleApplyDraft = useCallback(() => {
    if (!draft) return;
    onApplyDraft(draft);
    resetState();
    onClose();
  }, [draft, onApplyDraft, onClose, resetState]);

  const updateDraftField = useCallback((field: VoicePatientDraftField, value: string) => {
    setDraft((current) => {
      if (!current) return current;
      const nextValue = value.trim() ? value : undefined;
      return {
        ...current,
        [field]: field === "sex" && (nextValue === "M" || nextValue === "F") ? nextValue : nextValue,
        autofilledFields: Array.from(new Set([...(current.autofilledFields || []), field])),
      };
    });
  }, []);

  const hasDraft = !!draft;
  const transcriptValue = useMemo(() => transcript, [transcript]);
  const nextDraft = useMemo(() => draft || {}, [draft]);
  const recordingLabel =
    status === "idle"
      ? t('voiceReadyToRecord')
        : status === "recording"
          ? t('voiceRecording')
          : status === "paused"
            ? t('voicePaused')
            : status === "processing"
            ? t('voicePreparing')
            : status === "ready"
              ? t('voiceDraftReady')
              : t('voiceAttention');

  useEffect(() => {
    console.log("[VoiceIntake][performance]", {
      mode: isLowPerformanceMode ? "light" : "standard",
      transcriptLength: transcriptValue.length,
      chunksCount: chunksRef.current.length,
      isRecording: status === "recording",
    });
  }, [isLowPerformanceMode, status, transcriptValue.length]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[160] bg-slate-950/55 backdrop-blur-sm">
      <button
        type="button"
        aria-label={t('voiceClosePanel')}
        className="absolute inset-0 cursor-default bg-slate-950/20"
        onClick={cancelRecording}
      />

      <div className="absolute inset-0 flex items-end justify-center p-0 md:items-start md:justify-end md:p-4">
        <div
          className="relative flex w-full max-h-[90vh] flex-col overflow-hidden rounded-t-[28px] border border-cyan-500/20 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.12),_rgba(2,6,23,0.98)_45%)] shadow-[0_30px_120px_rgba(0,0,0,0.55)] md:w-[min(460px,calc(100vw-24px))] md:max-h-[calc(100vh-32px)] md:rounded-[30px]"
          style={{ width: "min(460px, calc(100vw - 24px))", maxHeight: "calc(100vh - 32px)" }}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent" />

          <div className="shrink-0 border-b border-white/5 px-4 py-4 sm:px-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-cyan-400/25 bg-cyan-500/10 text-cyan-200">
                  <Mic className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-200/70">{t('patientVoiceConsultation')}</p>
                  <h3 className="mt-1 truncate text-[20px] font-bold text-white">{t('voiceIntakeTitle')}</h3>
                  <p className="mt-1 line-clamp-2 text-[12px] leading-5 text-slate-300">
                    {t('voiceIntakeSubtitle')}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={cancelRecording}
                className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition hover:bg-white/10"
        aria-label={t('voiceCloseModal')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="shrink-0 border-b border-amber-400/15 bg-amber-500/10 px-4 py-3 text-xs text-amber-100 sm:px-5">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
              <p>{t('voiceClinicalDisclaimer')}</p>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
            <div className="space-y-4">
              <section className="rounded-[24px] border border-white/8 bg-slate-950/70 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{t('voiceRecordingSection')}</p>
                    <h4 className="mt-1 text-base font-semibold text-white">{t('voiceAnamnesisSupport')}</h4>
                  </div>
                  <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-slate-200">
                    <span className={`h-2.5 w-2.5 rounded-full ${status === "recording" ? "bg-emerald-400" : status === "paused" ? "bg-amber-300" : "bg-slate-500"}`} />
                    {recordingLabel}
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-cyan-400/15 bg-cyan-500/5 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-cyan-100/70">{t('voiceRecordedTime')}</p>
                  <p className="mt-2 font-mono text-2xl font-semibold text-white">{formatDuration(seconds)}</p>
                  <p className="mt-2 text-xs font-medium text-slate-300">{t('voiceStatus')}: {recordingLabel}</p>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={startRecording}
                    disabled={status === "recording" || isAnalyzing}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-400/20 bg-emerald-500/15 px-3 py-2.5 text-xs font-bold text-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Mic className="h-4 w-4" />
                    {t('voiceStart')}
                  </button>
                  <button
                    type="button"
                    onClick={status === "paused" ? resumeRecording : pauseRecording}
                    disabled={status === "idle" || status === "processing" || isAnalyzing}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2.5 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {status === "paused" ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                    {status === "paused" ? t('voiceResume') : t('voicePause')}
                  </button>
                  <button
                    type="button"
                    onClick={finalizeRecording}
                    disabled={status === "idle" || status === "processing" || isAnalyzing}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-400/20 bg-cyan-500/15 px-3 py-2.5 text-xs font-bold text-cyan-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Square className="h-4 w-4" />
                    {t('voiceFinish')}
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-200"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t('voiceCloseWithoutApply')}
                  </button>
                  <div className="rounded-2xl border border-white/8 bg-white/5 px-3 py-2 text-[11px] leading-5 text-slate-400">
                    {t('voiceAudioLocalHint')}
                  </div>
                </div>

                {error && (
                  <div className="mt-3 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-3 py-2.5 text-xs leading-5 text-amber-100">
                    {error}
                  </div>
                )}

                {audioUrl && (
                  <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-3">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{t('voiceLocalAudio')}</p>
                        <p className="mt-1 text-xs text-slate-200">{t('voiceLocalAudioReview')}</p>
                      </div>
                      <Bot className="h-5 w-5 text-cyan-300" />
                    </div>
                    <audio controls className="w-full">
                      <source src={audioUrl} />
                    </audio>
                  </div>
                )}
              </section>

              <section className="rounded-[24px] border border-white/8 bg-slate-950/70 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{t('voiceTranscription')}</p>
                    <h4 className="mt-1 text-base font-semibold text-white">{t('voiceAnalyzedText')}</h4>
                  </div>
                  <Sparkles className="h-5 w-5 text-cyan-300" />
                </div>

                <textarea
                  value={transcriptValue}
                  onChange={(event) => setTranscript(event.target.value)}
                  placeholder={t('voiceTranscriptPlaceholder')}
                  className={`mt-3 w-full rounded-3xl border border-white/10 bg-slate-950/80 px-3 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-400/40 ${isLowPerformanceMode ? "min-h-[150px]" : "min-h-[180px]"}`}
                />

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      if (!transcript.trim()) return;
                      setIsAnalyzing(true);
                      setError("");
                      try {
                        const resolvedDraft = await extractPatientDataFromTranscript(transcript);
                        setDraft(resolvedDraft);
                        setStatus("ready");
                      } catch (err) {
                        console.error("[PatientVoiceIntakeModal] failed to reanalyze transcript", err);
                        setError(t('voiceReanalyzeError'));
                      } finally {
                        setIsAnalyzing(false);
                      }
                    }}
                    disabled={!transcript.trim() || isAnalyzing}
                    className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/20 bg-cyan-500/15 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t('voiceReanalyzeText')}
                  </button>
                  <p className="text-[11px] leading-5 text-slate-500">
                    {status === "ready" ? t('voiceDraftReadyHint') : t('voiceDraftEditHint')}
                  </p>
                </div>
              </section>

              <section className="rounded-[24px] border border-white/8 bg-slate-950/70 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{t('voiceDraft')}</p>
                    <h4 className="mt-1 text-base font-semibold text-white">{t('voiceStructuredFields')}</h4>
                  </div>
                  <div className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100">
                    {hasDraft ? t('voiceReadyBadge') : t('voiceWaitingBadge')}
                  </div>
                </div>

                {!hasDraft ? (
                  <div className="mt-4 rounded-3xl border border-dashed border-white/10 bg-white/5 px-4 py-8 text-center text-slate-400">
                    <Mic className="mx-auto h-9 w-9 text-slate-500" />
                    <p className="mt-3 text-sm font-semibold text-slate-200">{t('voiceNoDraftYet')}</p>
                    <p className="mt-2 text-xs leading-5">{t('voiceFinalizeToFill')}</p>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {draftFields.map((field) => {
                      const value = nextDraft[field.key];
                      const hasValue = typeof value === "string" ? value.trim().length > 0 : !!value;
                      const confidence = draft?.confidence?.[field.key];
                      const isLowConfidence = !!draft?.lowConfidenceFields?.includes(field.key) || (confidence != null && confidence < 0.6 && hasValue);
                      const isAutofilled = !!draft?.autofilledFields?.includes(field.key) && hasValue;
                      const fieldValue = hasValue ? String(value) : "";
                      return (
                        <DraftFieldCard
                          key={String(field.key)}
                          field={field}
                          value={fieldValue}
                          confidence={confidence}
                          isLowConfidence={isLowConfidence}
                          isAutofilled={isAutofilled}
                          onChange={updateDraftField}
                        />
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="rounded-[24px] border border-cyan-400/15 bg-cyan-500/5 p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="mt-0.5 h-5 w-5 text-cyan-300" />
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-white">{t('voiceReadyBadge')} {t('voiceApplyPrompt')}</p>
                    <p className="text-sm leading-6 text-slate-300">
                      {t('voiceApplyDisclaimer')}
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className="shrink-0 border-t border-white/5 bg-slate-950/90 px-4 py-4 sm:px-5">
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleApplyDraft}
                disabled={!hasDraft || isAnalyzing}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-cyan-500 px-4 py-3 text-sm font-bold text-slate-950 transition-colors hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('voiceApplyToForm')}
              </button>
              <div className="flex items-center justify-between gap-3 text-[10px] text-slate-500">
                <span>{t('voiceLgpdNotice')}</span>
                <label className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-bold uppercase tracking-[0.18em] text-slate-300">
                  <input
                    type="checkbox"
                    checked={isLowPerformanceMode}
                    onChange={(event) => setIsLowPerformanceMode(event.target.checked)}
                  />
                  Modo leve
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientVoiceIntakeModal;

